import type { ChangeEvent, Dispatch, FC, SetStateAction } from 'react'
import {
  CwsAlert,
  CwsGrid,
  CwsGridCol,
  CwsGridRow,
  CwsH3,
  CwsIcon,
  CwsInput,
  CwsLabel,
  CwsP,
  CwsSelect,
  CwsSelectOption,
} from '@components/cws'
import { useTranslate } from '@hooks/useTranslation'
import type { CountryName } from '@model/locales'
import { DELIVER_TO_COUNTRIES } from '@model/locales'
import type { CustomerInfo } from '@hooks/useReturnOrders'
import * as yup from 'yup'
import { validations } from '@utils/order'
import { useEffect, useState } from 'react'

interface PersonalDetailsProps {
  customerInfo?: CustomerInfo
  setCustomerInfo: Dispatch<SetStateAction<CustomerInfo>>
  deliveryCountry?: CountryName
  isReturnEmail?: boolean
}
export const PersonalDetails: FC<PersonalDetailsProps> = ({
  customerInfo,
  setCustomerInfo,
  deliveryCountry,
  isReturnEmail,
}) => {
  const { translate } = useTranslate()
  const [isValidPhone, setIsValidPhone] = useState(false)
  const handleChangeFirstName = (value: string) => {
    setCustomerInfo((prevState) => ({ ...prevState, firstName: value }))
  }
  const handleChangeLastName = (value: string) => {
    setCustomerInfo((prevState) => ({ ...prevState, lastName: value }))
  }
  const handleChangeCountry = (country: CountryName) => {
    setCustomerInfo((prevState) => ({ ...prevState, country }))
  }
  const handleChangeEmail = (value: string) => {
    setCustomerInfo((prevState) => ({ ...prevState, emailAddress: value }))
  }
  const handleChangePhone = (value: string) => {
    setCustomerInfo((prevState) => ({ ...prevState, phone: value }))
  }
  useEffect(() => {
    const checkValidPhone = yup.string().defined().matches(validations.telephone, { message: 'Telephone is invalid' })
    checkValidPhone.isValid(customerInfo?.phone).then((res) => setIsValidPhone(res))
  }, [customerInfo?.phone])

  const handleChangeAddress = (value: string) => {
    setCustomerInfo((prevState) => ({ ...prevState, streetAddress: value }))
  }
  return (
    <div className="cws-bg-color-white">
      <CwsGrid>
        <CwsGridRow>
          <CwsGridCol col="12" className="cws-px-0 cws-py-m" cw>
            <CwsGridRow>
              <CwsGridCol col={12} className="flex gap-2 items-center">
                <CwsIcon size="24px" icon="file-personal" />
                <CwsH3 className="cws-m-0">{translate('return.form.section.header.client')}</CwsH3>
              </CwsGridCol>

              <CwsGridCol col={12} lg={6}>
                <CwsP size="small">{translate('profile.details.firstname')}:</CwsP>
                <CwsInput onChange={handleChangeFirstName} value={customerInfo?.firstName} id={'returnFirstName'} />
              </CwsGridCol>
              <CwsGridCol col={12} lg={6}>
                <CwsP size="small">{translate('profile.details.lastname')}:</CwsP>
                <CwsInput onChange={handleChangeLastName} value={customerInfo?.lastName} id={'returnLastName'} />
              </CwsGridCol>
              <CwsGridCol col={12} lg={6}>
                <CwsP size="small">{translate('cart.confirmation.vendor.email.label')}:</CwsP>
                <CwsInput onChange={handleChangeEmail} value={customerInfo?.emailAddress} id={'returnEmail'} />
              </CwsGridCol>
              <CwsGridCol col={12} lg={6}>
                <CwsP size="small">{translate('w2.person.contact.data.telephone')}:</CwsP>
                <CwsInput
                  invalid={!isValidPhone}
                  onChange={handleChangePhone}
                  value={customerInfo?.phone}
                  id={'returnPhone'}
                />
              </CwsGridCol>
              {isReturnEmail && (
                <CwsGridCol col={8} lg={8}>
                  <CwsP size="small">{translate('return.form.address')}:</CwsP>
                  <CwsInput onChange={handleChangeAddress} value={customerInfo?.streetAddress} />
                  <CwsAlert variant="inline" type="info">
                    <CwsP size="small">{translate('return.form.address.hint')}</CwsP>
                  </CwsAlert>
                </CwsGridCol>
              )}

              <CwsGridCol col={isReturnEmail ? 4 : 6}>
                <CwsLabel>{`${translate('basket.courier.selection.country')}:`}</CwsLabel>
                <CwsSelect
                  value={customerInfo?.country}
                  disabled={deliveryCountry ? customerInfo?.country : false}
                  onChange={handleChangeCountry}
                  id="countries"
                >
                  <CwsSelectOption value="" />
                  {DELIVER_TO_COUNTRIES.filter((country) => (deliveryCountry ? country === deliveryCountry : true)).map(
                    (country) => (
                      <CwsSelectOption key={country} value={country} title={translate(country)} />
                    )
                  )}
                </CwsSelect>
              </CwsGridCol>
            </CwsGridRow>
          </CwsGridCol>
        </CwsGridRow>
      </CwsGrid>
    </div>
  )
}
